import React, { useEffect, useMemo, useState } from 'react';
import { Navigation, MapPin, Clock, Loader2, X } from 'lucide-react';
import { getPrayerTimes, qiblaBearing, getActivePrayer, formatHM } from '../utils/prayerTimes';
import { useZakatGlobal } from '../context/ZakatGlobalContext';
import { secureStorage } from '../utils/secureStorage';

/**
 * PrayerCompass — a compact Qibla + prayer-time widget.
 *
 * Zero-network, privacy-first:
 *   • Attempts navigator.geolocation (user grants once; coords cached in
 *     localStorage so returning users don't re-prompt).
 *   • Falls back to the capital-city coords of the user's selected
 *     country from ZakatGlobalContext.
 *   • Prayer times computed in-browser (MWL angles — Fajr 18°, Isha 17°,
 *     Shafi'i Asr). Qibla is a great-circle bearing to the Kaaba.
 *
 * Not meant to replace a dedicated prayer-time app — displayed with a
 * clear "approximate — verify locally" note. The point is to give
 * returning users a quick glance so they install the PWA.
 */

const LS_COORDS = 'amanah_prayer_coords_v1';

// Minimal lat/lon table for the 25 most-visited countries. Enough for a
// sensible fallback when geolocation is denied / unavailable.
const COUNTRY_FALLBACK = {
  SA: [24.7136, 46.6753], AE: [24.4539, 54.3773], QA: [25.2854, 51.5310],
  KW: [29.3759, 47.9774], BH: [26.0667, 50.5577], OM: [23.5880, 58.3829],
  JO: [31.9454, 35.9284], LB: [33.8886, 35.4955], SY: [33.5138, 36.2765],
  IQ: [33.3152, 44.3661], YE: [15.3694, 44.1910], EG: [30.0444, 31.2357],
  LY: [32.8872, 13.1913], TN: [36.8065, 10.1815], DZ: [36.7538, 3.0588],
  MA: [34.0209, -6.8416], SD: [15.5007, 32.5599],
  TR: [41.0082, 28.9784], IR: [35.6892, 51.3890],
  PK: [33.6844, 73.0479], IN: [28.6139, 77.2090], BD: [23.8103, 90.4125],
  MY: [3.1390, 101.6869], ID: [-6.2088, 106.8456], SG: [1.3521, 103.8198],
  US: [38.9072, -77.0369], GB: [51.5074, -0.1278], CA: [43.6532, -79.3832],
  FR: [48.8566, 2.3522], DE: [52.5200, 13.4050], NL: [52.3676, 4.9041],
  AU: [-33.8688, 151.2093], ZA: [-29.8587, 31.0218], NG: [9.0765, 7.3986],
};

export const PrayerCompass = ({ testid = 'prayer-compass', onDismiss }) => {
  const { selectedCountry } = useZakatGlobal();
  const [coords, setCoords] = useState(() => {
    try {
      const raw = secureStorage.getItem(LS_COORDS);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed.lat === 'number' && typeof parsed.lon === 'number') return parsed;
    } catch { /* ignore */ }
    return null;
  });
  const [geoBusy, setGeoBusy] = useState(false);
  const [geoError, setGeoError] = useState('');
  const [now, setNow] = useState(() => new Date());

  // Fall back to country capital if the user hasn't shared location.
  const activeCoords = useMemo(() => {
    if (coords) return coords;
    const code = selectedCountry?.code;
    if (code && COUNTRY_FALLBACK[code]) {
      const [lat, lon] = COUNTRY_FALLBACK[code];
      return { lat, lon, source: 'country' };
    }
    return null;
  }, [coords, selectedCountry]);

  // Tick every minute so the "next prayer in" countdown refreshes.
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60 * 1000);
    return () => clearInterval(id);
  }, []);

  const times = useMemo(() => activeCoords ? getPrayerTimes({ lat: activeCoords.lat, lon: activeCoords.lon, date: now }) : null, [activeCoords, now]);
  const active = useMemo(() => getActivePrayer(times, now), [times, now]);
  const bearing = useMemo(() => activeCoords ? qiblaBearing({ lat: activeCoords.lat, lon: activeCoords.lon }) : null, [activeCoords]);

  const requestLocation = () => {
    if (!navigator.geolocation) { setGeoError('Geolocation not supported'); return; }
    setGeoBusy(true);
    setGeoError('');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const next = { lat: pos.coords.latitude, lon: pos.coords.longitude, source: 'device' };
        try { secureStorage.setItem(LS_COORDS, JSON.stringify(next)); } catch { /* ignore */ }
        setCoords(next);
        setGeoBusy(false);
      },
      () => { setGeoError('Permission denied — using country default.'); setGeoBusy(false); },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 60 * 60 * 1000 },
    );
  };

  const nextLabel = active?.next?.label || '—';
  const nextTime = active?.nextAt ? formatHM(active.nextAt) : '—';
  const minsTo = active?.nextAt ? Math.max(0, Math.round((active.nextAt.getTime() - now.getTime()) / 60000)) : null;

  return (
    <section
      data-testid={testid}
      aria-label="Prayer times and Qibla direction"
      className="relative overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-[0_4px_20px_-2px_rgba(10,25,47,0.06)]"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-stone-200 bg-[#FAF8F5]">
        <div className="flex items-center gap-2 min-w-0">
          <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-emerald-700/10 text-emerald-700">
            <Navigation className="w-3.5 h-3.5" aria-hidden />
          </span>
          <div className="min-w-0">
            <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-gold-600 font-semibold">Prayer compass</div>
            <div className="text-[13px] text-stone-700 truncate">
              {activeCoords?.source === 'device' ? 'Your location' : (selectedCountry?.name || 'Approximate')}
              <span className="text-stone-400"> · MWL method</span>
            </div>
          </div>
        </div>
        {onDismiss && (
          <button
            type="button"
            onClick={onDismiss}
            data-testid={`${testid}-dismiss`}
            aria-label="Dismiss prayer compass"
            className="text-stone-400 hover:text-stone-700 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Body */}
      <div className="p-5 grid grid-cols-1 sm:grid-cols-[auto,1fr] gap-5 items-center">
        {/* Qibla dial */}
        <div className="relative mx-auto">
          <div className="relative w-28 h-28 rounded-full border-2 border-stone-200 bg-[#FAF8F5] flex items-center justify-center overflow-hidden">
            {/* Cardinal letters */}
            <span className="absolute top-1.5 text-[9px] font-mono text-stone-400 font-semibold">N</span>
            <span className="absolute bottom-1.5 text-[9px] font-mono text-stone-400 font-semibold">S</span>
            <span className="absolute left-1.5 text-[9px] font-mono text-stone-400 font-semibold">W</span>
            <span className="absolute right-1.5 text-[9px] font-mono text-stone-400 font-semibold">E</span>
            {/* Qibla needle */}
            {bearing != null && (
              <div
                className="absolute w-0.5 h-12 bg-emerald-700 origin-bottom"
                style={{ bottom: '50%', transform: `translateX(-50%) rotate(${bearing}deg)`, left: '50%' }}
                aria-hidden
              >
                <span className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 rounded-full bg-emerald-700" />
              </div>
            )}
            <div className="w-2 h-2 rounded-full bg-stone-400" aria-hidden />
          </div>
          <div className="mt-2 text-center">
            <div className="text-[10px] font-mono uppercase tracking-[0.14em] text-stone-500">Qibla</div>
            <div data-testid={`${testid}-qibla-deg`} className="font-mono tabular-nums text-[14px] text-emerald-700 font-semibold">
              {bearing != null ? `${bearing.toFixed(1)}°` : '—'}
            </div>
          </div>
        </div>

        {/* Next prayer + today's grid */}
        <div className="min-w-0">
          {activeCoords ? (
            <>
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-gold-600 font-semibold">Next prayer</span>
                <span className="text-[11px] text-stone-500">
                  {minsTo != null ? `in ${minsTo < 60 ? `${minsTo}m` : `${Math.floor(minsTo / 60)}h ${minsTo % 60}m`}` : ''}
                </span>
              </div>
              <div className="flex items-baseline gap-3 mt-1">
                <span data-testid={`${testid}-next-name`} className="font-heading text-xl text-[#0D1520]">{nextLabel}</span>
                <span data-testid={`${testid}-next-time`} className="font-mono tabular-nums font-semibold text-emerald-700 text-xl">{nextTime}</span>
              </div>

              {/* Today's full grid */}
              <div className="mt-3 grid grid-cols-3 gap-1.5" data-testid={`${testid}-grid`}>
                {['fajr', 'dhuhr', 'asr', 'maghrib', 'isha'].map((k) => {
                  const isNext = active?.next?.key === k;
                  const label = { fajr: 'Fajr', dhuhr: 'Dhuhr', asr: 'Asr', maghrib: 'Maghrib', isha: 'Isha' }[k];
                  return (
                    <div
                      key={k}
                      data-testid={`${testid}-time-${k}`}
                      className={`rounded-md px-2 py-1.5 text-left border ${isNext ? 'bg-emerald-700/5 border-emerald-700/30' : 'bg-white border-stone-200'}`}
                    >
                      <div className="text-[9.5px] font-mono uppercase tracking-wide text-stone-500">{label}</div>
                      <div className={`font-mono tabular-nums text-[12px] font-semibold ${isNext ? 'text-emerald-700' : 'text-[#0D1520]'}`}>
                        {formatHM(times?.[k])}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <p className="text-sm text-stone-600 leading-relaxed">
              Pick a country or share your location to see prayer times and the Qibla direction.
            </p>
          )}

          {/* Location CTA */}
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={requestLocation}
              disabled={geoBusy}
              data-testid={`${testid}-use-location`}
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-stone-300 hover:border-emerald-700 hover:bg-emerald-700/5 text-[12px] text-stone-700 font-medium transition-colors disabled:opacity-60"
            >
              {geoBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <MapPin className="w-3.5 h-3.5" />}
              {coords ? 'Refresh location' : 'Use my location'}
            </button>
            {geoError && <span className="text-[11px] text-stone-500">{geoError}</span>}
          </div>
          <p className="mt-2 text-[10.5px] text-stone-500 italic flex items-start gap-1 leading-snug">
            <Clock className="w-3 h-3 mt-0.5 shrink-0" aria-hidden />
            Approximate — verify with your local masjid or almanac before relying on these times.
          </p>
        </div>
      </div>
    </section>
  );
};

export default PrayerCompass;
